import { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Upload, X, Image as ImageIcon } from "lucide-react";

/**
 * ImageUploader — uploads a file via POST /api/admin/upload
 * Calls onChange with the returned public URL (e.g. "/api/files/akyvia/uploads/<uuid>.png").
 */
export const ImageUploader = ({ value, onChange, label = "Image" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const previewSrc = value
    ? (value.startsWith("http") || value.startsWith("data:") ? value : `${backend}${value}`)
    : null;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5 MB).");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid="image-uploader">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <div className="mt-1.5 flex items-start gap-4">
        {previewSrc ? (
          <div className="relative w-32 h-32 rounded-md overflow-hidden border border-slate-200 bg-slate-50 flex-shrink-0">
            <img src={previewSrc} alt="preview" className="w-full h-full object-cover" />
            <button
              type="button"
              data-testid="image-uploader-remove"
              onClick={() => onChange("")}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 border border-slate-200 flex items-center justify-center hover:bg-rose-50"
              title="Remove image"
            >
              <X className="w-3.5 h-3.5 text-rose-600" />
            </button>
          </div>
        ) : (
          <div className="w-32 h-32 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-slate-400 flex-shrink-0">
            <ImageIcon className="w-6 h-6" strokeWidth={1.5} />
            <span className="text-[10px] uppercase tracking-widest mt-1">No image</span>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
            onChange={onFile}
            className="hidden"
            data-testid="image-uploader-input"
          />
          <button
            type="button"
            data-testid="image-uploader-button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#0B1B3D] hover:text-[#0B1B3D] text-slate-700 text-sm px-4 py-2.5 rounded-md disabled:opacity-60"
          >
            <Upload className="w-4 h-4" strokeWidth={1.5} />
            {uploading ? "Uploading…" : previewSrc ? "Replace image" : "Upload image"}
          </button>
          <p className="mt-2 text-xs text-slate-500 break-all">
            {value ? value : "PNG, JPG, WebP up to 5 MB."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;
